import React, { useEffect } from "react";
import { Grid, Button, TextField, Typography } from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import clsx from "clsx";
import useCurrentFacilityTimezone from "../../../hooks/useCurrentFacilityTimezone";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import useCredentialScanner from "../../../hooks/useCredentialScanner";
import { useStyles } from "./RefundTransactionSearch.styles";

const MAX_DATE_RANGE_DAYS = 60;

const refundSearchSchema = Yup.object().shape({
  cardLast4: Yup.string()
    .trim()
    .matches(/^\d{4}$|^$/, "Must require 4-digits"),
  transactionID: Yup.string()
    .trim()
    .matches(/^[0-9]*$/, "Transaction ID must contain only digits"),
  transactionDate: Yup.date()
    .nullable()
    .test(
      "date-range",
      `Transaction date cannot be more than ${MAX_DATE_RANGE_DAYS} days ago`,
      function(transactionDate) {
        if (!transactionDate) return true;
        const daysDiff = moment().diff(moment(transactionDate), "days");
        return daysDiff <= MAX_DATE_RANGE_DAYS;
      }
    ),
});

const RefundTransactionSearch = ({ onSearch, loading }) => {
  const classes = useStyles();
  const { timeZone } = useCurrentFacilityTimezone();
  const enqueueSnackbar = useEnqueueSnackbar();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(refundSearchSchema),
    defaultValues: {
      ticketNumber: "",
      cardLast4: "",
      transactionID: "",
      transactionDate: null,
    },
    mode: "onChange",
  });

  const { Reader, credentialRead } = useCredentialScanner({
    readerStyle: classes,
    ticketsOnly: false,
    toggleButtonTextOn: "Stop Scanning",
    toggleButtonTextOff: "Scan Ticket",
  });

  useEffect(() => {
    if (credentialRead) {
      setValue("ticketNumber", credentialRead);
    }
  }, [credentialRead, setValue]);

  const isSearchDisabled = () => {
    return Object.keys(errors).length > 0;
  };

  const hasAtLeastOneSearchCriteria = (data) => {
    const { ticketNumber, cardLast4, transactionID, transactionDate } = data;
    return (
      ticketNumber.trim().length > 0 ||
      cardLast4.trim().length > 0 ||
      transactionID.trim().length > 0 ||
      transactionDate !== null
    );
  };

  const onSubmit = (data) => {
    if (!hasAtLeastOneSearchCriteria(data)) {
      enqueueSnackbar("Please enter at least one search criteria", {
        variant: "error",
        tag: "refundSearchNoCriteria",
      });
      return;
    }

    const startTransactionDateTime = data.transactionDate
      ? moment(data.transactionDate)
          .tz(timeZone)
          .startOf("day")
          .utc()
          .toISOString()
      : null;
    const endTransactionDateTime = data.transactionDate
      ? moment(data.transactionDate)
          .tz(timeZone)
          .endOf("day")
          .utc()
          .toISOString()
      : null;

    onSearch({
      ticketNumber: data.ticketNumber.trim(),
      cardLast4: data.cardLast4.trim(),
      transactionID: data.transactionID.trim(),
      startTransactionDateTime,
      endTransactionDateTime,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container className={clsx(classes.scannerContainer)}>
        <Grid item xs={12} className={clsx(classes.scannerGrid)}>
          <Typography variant="h5" className={clsx(classes.scannerTitle)}>
            Transaction Search
          </Typography>
          <Reader />
        </Grid>
      </Grid>

      <Grid
        container
        alignItems="center"
        className={clsx(classes.inputsGrid, "refund-transaction-search")}
      >
        <Grid item xs={12} className={clsx(classes.searchLabel)}>
          <Typography variant="body2">
            Or search by ticket number or transaction details
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={3} className={clsx(classes.inputGrid)}>
          <Controller
            name="ticketNumber"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                id="ticketNumber"
                label="Ticket Number"
                variant="standard"
                fullWidth
                error={!!error}
                helperText={error?.message}
                className={clsx("ticket-number-input")}
                inputProps={{
                  maxLength: 50,
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3} className={clsx(classes.inputGrid)}>
          <Controller
            name="cardLast4"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                id="cardLast4"
                label="Last 4 of CC #"
                variant="standard"
                fullWidth
                error={!!error}
                helperText={error?.message}
                className={clsx("card-last4-input")}
                inputProps={{
                  maxLength: 4,
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3} className={clsx(classes.inputGrid)}>
          <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
            <Controller
              name="transactionDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <KeyboardDatePicker
                  id="transactionDate"
                  label="Transaction Date"
                  disableFuture
                  format={"MM/DD/YYYY"}
                  clearable
                  fullWidth
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={!!error}
                  helperText={error?.message}
                  className={clsx("transaction-date-picker")}
                />
              )}
            />
          </MuiPickersUtilsProvider>
        </Grid>

        <Grid item xs={12} sm={6} md={3} className={clsx(classes.inputGrid)}>
          <Controller
            name="transactionID"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                id="transactionID"
                label="Transaction ID"
                variant="standard"
                fullWidth
                error={!!error}
                helperText={error?.message}
                className={clsx("transaction-id-input")}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} className={clsx(classes.buttonGrid)}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || isSearchDisabled()}
            className={clsx(classes.searchButton, "search-button")}
          >
            SEARCH
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default RefundTransactionSearch;
